<script setup lang="ts">
import { toTypedSchema } from '@vee-validate/zod'
import { useField, useForm } from 'vee-validate'
import { onMounted, reactive, watch } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import { z as zod } from 'zod'

import { useZodValidation } from '@/composables/zodValidation'
import store from '@/store'

interface LoginState {
  errorMessage: string
  password: string
  showPassword: boolean
  username: string
}

interface Props {
  loginName?: string
  successMessage?: string
}

const props = withDefaults(defineProps<Props>(), {
  loginName: '',
  successMessage: '',
})

const state = reactive<LoginState>({
  errorMessage: '',
  password: '',
  showPassword: false,
  username: '',
})

const { nonEmptyString } = useZodValidation()

const { errors, handleSubmit, meta } = useForm({
  validationSchema: toTypedSchema(
    zod.object({
      password: nonEmptyString,
      username: nonEmptyString,
    }),
  ),
})

const { value: username } = useField('username')
const { value: password } = useField('password')

const route = useRoute()
const router = useRouter()

onMounted(() => {
  if (props.loginName) {
    state.username = props.loginName
  }
  state.username ||= ''
  state.password ||= ''

  // bridge between vee-validate and the component's state
  watch(
    () => state.username,
    () => {
      username.value = state.username
    },
  )

  watch(
    () => state.password,
    () => {
      password.value = state.password
    },
  )
})

const onSubmit = handleSubmit((values) => {
  state.errorMessage = ''
  store.dispatch.user
    .login({
      password: values.password,
      showError: (message) => {
        state.errorMessage = message
      },
      username: values.username,
    })
    .then((ok) => {
      if (ok) {
        const redirect = route.query.redirect as string
        router.push(redirect || '/')
      }
    })
})
</script>

<template>
  <v-card class="mx-auto mt-12" elevation="2" max-width="480">
    <div class="login-header">Login</div>
    <v-card-text>
      <v-alert v-if="props.successMessage" class="mb-4" closable density="compact" type="success">
        {{ props.successMessage }}
      </v-alert>
      <v-alert v-if="state.errorMessage" class="mb-4" closable density="compact" type="error" @click:close="state.errorMessage = ''">
        {{ state.errorMessage }}
      </v-alert>
      <v-form id="form" @submit.prevent="onSubmit">
        <v-row>
          <v-col cols="12">
            <v-text-field
              v-model="username"
              class="input-group--focused"
              :error-messages="errors.username"
              label="Username"
              variant="underlined"
            />
          </v-col>
        </v-row>
        <v-row>
          <v-col cols="12">
            <v-text-field
              v-model="password"
              :append-icon="state.showPassword ? 'mdi-eye' : 'mdi-eye-off'"
              :error-messages="errors.password"
              label="Password"
              :type="state.showPassword ? 'text' : 'password'"
              variant="underlined"
              @click:append="state.showPassword = !state.showPassword"
            />
            <v-row class="mt-3 mx-0 px-0" justify="space-between">
              <v-col class="mx-0 px-0" md="4">
                <v-btn block color="primary" :disabled="!meta.valid" type="submit">Sign in</v-btn>
              </v-col>
              <v-col class="text-right mx-0 px-0" md="6">
                <v-btn
                  variant="plain"
                  @click="
                    () => {
                      $router.push({ name: 'forgot-password' })
                    }
                  "
                  >I forgot my password</v-btn
                >
              </v-col>
            </v-row>
          </v-col>
        </v-row>
      </v-form>
    </v-card-text>
  </v-card>
</template>

<style scoped>
.login-header {
  background: rgb(var(--v-theme-primary));
  color: #fff;
  padding: 14px 20px;
  font-size: 16px;
  font-weight: 500;
  letter-spacing: 0.5px;
}
</style>
